import React, { useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import "./Backtesting.css";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const STRATEGIES = [
  "SMA",
  "MACD",
  "RSI",
  "BollingerBands",
  "Stochastic Oscillator",
];

// Chart.js plugin to draw ▲ / ▼ signal markers directly on the canvas
const signalMarkerPlugin = {
  id: "signalMarkers",
  afterDatasetsDraw(chart, _args, options) {
    if (!options.signals || !options.signals.length || !options.showSignals) return;
    // Guard: only run on charts that explicitly opt in via plugin options
    if (!options.enabled) return;

    const { ctx, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    const labels = chart.data.labels;

    options.signals.forEach(({ date, type, price }) => {
      const xIndex = labels.indexOf(date);
      if (xIndex === -1) return;

      const x = xScale.getPixelForValue(xIndex);
      const y = yScale.getPixelForValue(price);

      const isBuy = type === "buy";
      const color = isBuy ? "#00e676" : "#ff1744";
      const arrowSize = 7;
      const yOffset = isBuy ? arrowSize + 6 : -(arrowSize + 6);

      ctx.save();
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.beginPath();
      if (isBuy) {
        ctx.moveTo(x, y + yOffset - arrowSize);
        ctx.lineTo(x - arrowSize, y + yOffset + arrowSize);
        ctx.lineTo(x + arrowSize, y + yOffset + arrowSize);
      } else {
        ctx.moveTo(x, y + yOffset + arrowSize);
        ctx.lineTo(x - arrowSize, y + yOffset - arrowSize);
        ctx.lineTo(x + arrowSize, y + yOffset - arrowSize);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });
  },
};

ChartJS.register(signalMarkerPlugin);

// Compute max drawdown from equity curve
const computeMaxDrawdown = (equityCurve) => {
  if (!equityCurve || equityCurve.length === 0) return 0;
  let peak = equityCurve[0];
  let maxDD = 0;
  for (const val of equityCurve) {
    if (val > peak) peak = val;
    const dd = (peak - val) / peak;
    if (dd > maxDD) maxDD = dd;
  }
  return maxDD;
};

// Estimate win rate: fraction of sell signals where equity was higher than the preceding buy
const computeWinRate = (signals, equityCurve, dates) => {
  if (!signals || signals.length === 0 || !equityCurve || !dates) return null;
  const dateIndex = (d) => dates.indexOf(d);
  const buys = signals.filter((s) => s.type === "buy");
  const sells = signals.filter((s) => s.type === "sell");
  if (buys.length === 0 || sells.length === 0) return null;

  let wins = 0;
  let total = 0;
  sells.forEach((sell, i) => {
    const buy = buys[i];
    if (!buy) return;
    total++;
    if (sell.price > buy.price) wins++;
  });
  return total > 0 ? (wins / total) * 100 : null;
};

const INITIAL_CAPITAL = 10000;

const Backtesting = () => {
  const [formData, setFormData] = useState({ ticker: "", strategy: "SMA" });
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [dates, setDates] = useState([]);
  const [signals, setSignals] = useState([]);
  const [strategiesData, setStrategiesData] = useState({});
  const [appliedConfig, setAppliedConfig] = useState(null);
  const [showSignals, setShowSignals] = useState(true);

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const apiUrl = `https://api.stockastix.com/backtrading/backtest/?ticker=${formData.ticker}&strategy=${formData.strategy}`;

    try {
      const response = await axios.get(apiUrl);
      const { prices, dates, signals, strategies_data } = response.data;

      setPrices(prices);
      setDates(dates);
      setSignals(signals || []);
      setStrategiesData(strategies_data);
      setShowSignals(true);

      setAppliedConfig({
        ticker: formData.ticker,
        strategy: formData.strategy,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sharedScaleOptions = {
    x: {
      ticks: { color: "#ffffff", maxTicksLimit: 5 },
    },
    y: {
      ticks: { color: "#ffffff", maxTicksLimit: 6 },
      grid: { color: "#39393990" },
    },
  };

  const priceOptions = {
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      signalMarkers: { signals, showSignals, enabled: true },
      tooltip: {
        callbacks: {
          afterBody: (items) => {
            if (!showSignals || !signals.length) return [];
            const date = items[0]?.label;
            const hit = signals.find((s) => s.date === date);
            if (!hit) return [];
            return [`${hit.type === "buy" ? "▲ BUY" : "▼ SELL"} @ $${hit.price.toFixed(2)}`];
          },
        },
      },
    },
    scales: {
      ...sharedScaleOptions,
      y: {
        ...sharedScaleOptions.y,
        title: { display: true, text: "Price", color: "#ffffff" },
      },
    },
    elements: { point: { radius: 0 } },
  };

  const priceChartData = {
    labels: dates,
    datasets: [
      {
        label: `${appliedConfig?.ticker} Price`,
        data: prices,
        borderColor: "#6ea8fe",
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
        fill: false,
        backgroundColor: "transparent",
      },
    ],
  };

  const filteredEquity = strategiesData?.equity_curve || [];
  const finalValue = filteredEquity[filteredEquity.length - 1] || INITIAL_CAPITAL;
  const equityColor = finalValue >= INITIAL_CAPITAL ? "rgba(0, 255, 127, 1)" : "rgba(255, 29, 29, 1)";
  const ebgcolor = finalValue >= INITIAL_CAPITAL ? "rgba(0, 255, 127, 0.15)" : "rgba(251, 72, 72, 0.15)";

  const startDate = dates.length > 0 ? dates[0] : null;
  const endDate = dates.length > 0 ? dates[dates.length - 1] : null;

  const equityOptions = {
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { display: false } },
    scales: {
      ...sharedScaleOptions,
      y: {
        ...sharedScaleOptions.y,
        title: { display: true, text: "Portfolio Value ($)", color: "#ffffff" },
      },
    },
    elements: { point: { radius: 0 } },
  };

  const resultChartData = {
    labels: dates,
    datasets: [
      {
        label: `${appliedConfig?.strategy} Equity Curve`,
        data: filteredEquity,
        fill: true,
        borderWidth: 1.5,
        pointRadius: 0,
        borderColor: equityColor,
        backgroundColor: ebgcolor,
      },
    ],
  };

  const buyCount = signals.filter((s) => s.type === "buy").length;
  const sellCount = signals.filter((s) => s.type === "sell").length;

  // --- Summary metrics ---
  const totalReturn = ((finalValue - INITIAL_CAPITAL) / INITIAL_CAPITAL) * 100;
  const isProfit = totalReturn >= 0;
  const maxDrawdown = computeMaxDrawdown(filteredEquity);
  const winRate = computeWinRate(signals, filteredEquity, dates);
  const totalTrades = buyCount + sellCount;

  const summaryMetrics = appliedConfig && filteredEquity.length > 0 ? [
    {
      label: "Total Return",
      value: `${isProfit ? "+" : ""}${totalReturn.toFixed(2)}%`,
      sub: `$${INITIAL_CAPITAL.toLocaleString()} → $${finalValue.toFixed(2)}`,
      accent: isProfit ? "positive" : "negative",
    },
    {
      label: "Final Value",
      value: `$${finalValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      sub: `Initial: $${INITIAL_CAPITAL.toLocaleString()}`,
      accent: isProfit ? "positive" : "negative",
    },
    {
      label: "Max Drawdown",
      value: `${(maxDrawdown * 100).toFixed(2)}%`,
      sub: "Peak-to-trough decline",
      accent: maxDrawdown > 0.2 ? "negative" : "neutral",
    },
    {
      label: "Total Trades",
      value: totalTrades,
      sub: `▲ ${buyCount} buy  ·  ▼ ${sellCount} sell`,
      accent: "neutral",
    },
    ...(winRate !== null
      ? [{
          label: "Win Rate",
          value: `${winRate.toFixed(1)}%`,
          sub: "Profitable sell signals",
          accent: winRate >= 50 ? "positive" : "negative",
        }]
      : []),
  ] : [];

  return (
    <div className="backtesting-container">
      <h1 className="page-title">Strategy Backtesting</h1>

      <form className="backtesting-strip" onSubmit={handleFormSubmit}>
        <div className="strip-section">
          <input
            type="text"
            placeholder="Ticker (e.g., AAPL)"
            value={formData.ticker}
            onChange={(e) => handleFormChange("ticker", e.target.value.toUpperCase())}
          />
        </div>

        <div className="strip-section">
          <select
            value={formData.strategy}
            onChange={(e) => handleFormChange("strategy", e.target.value)}
          >
            {STRATEGIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="strip-section button-section">
          <button type="submit" className="run-btn" disabled={loading}>
            Run
          </button>
        </div>
      </form>

      {loading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}

      {appliedConfig && prices.length > 0 && (
        <>
          <div className="charts-wrapper">
            {/* Price chart */}
            <div className="chart-card">
              <div className="chart-card-header">
                <h5>{appliedConfig?.ticker} Price Chart</h5>
                <div className="chart-card-header-right">
                  {signals.length > 0 && (
                    <div className="signal-counts">
                      <span className="signal-buyy">▲ {buyCount}</span>
                      <span className="signal-sell">▼ {sellCount}</span>
                    </div>
                  )}
                  <button
                    className={`signals-toggle ${showSignals ? "activee" : ""}`}
                    onClick={() => setShowSignals((v) => !v)}
                    title="Toggle buy/sell signals"
                  >
                    {showSignals ? "Hide Signals" : "Show Signals"}
                  </button>
                </div>
              </div>
              <div className="backtest-range">Period: Last 1 Year</div>
              <Line data={priceChartData} options={priceOptions} height={120} />
            </div>

            {/* Equity chart */}
            <div className="chart-card">
              <div className="chart-card-header">
                <h5>Equity Curve <i>({appliedConfig?.strategy})</i></h5>
                {startDate && endDate && (
                  <div className="backtest-range">
                    Backtest period: {startDate} → {endDate}
                  </div>
                )}
              </div>
              <Line data={resultChartData} options={equityOptions} height={120} />
            </div>
          </div>

          {/* Strategy Summary */}
          {summaryMetrics.length > 0 && (
            <div className="summary-section">
              <div className="summary-header">
                <span className="summary-title">Strategy Summary</span>
                <span className="summary-badge">
                  {appliedConfig.ticker} · {appliedConfig.strategy}
                </span>
              </div>
              <div className="summary-grid">
                {summaryMetrics.map((metric) => (
                  <div className="summary-card" key={metric.label}>
                    <span className="summary-label">{metric.label}</span>
                    <span className={`summary-value accent-${metric.accent}`}>
                      {metric.value}
                    </span>
                    <span className="summary-sub">{metric.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Backtesting;